import { useEffect } from "react";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { app } from "../firebase";
import {
  registerDevice,
  unregisterDevice,
  toApiLocale,
} from "../api/pushServices";
import { store } from "../redux/store";
import { getLoggedInUserDataFromLocalStorage } from "../config/config";
import { getLanguageInLocalStorage } from "./useLanguagePercistant";
import { onSessionExpired } from "../utils/sessionEvents";
import { fetchNotifications } from "../redux/features/notificationsSlice";

// Push notifications are delivered by Firebase Cloud Messaging over two
// different transports, and which one applies is decided at runtime:
//
//   iOS    - the app is a WKWebView shell. There is no service-worker push in
//            WKWebView, so the native side owns FCM and this module talks to it
//            over the message-handler bridge described below.
//   Android- the Play Store app is a Trusted Web Activity, so there is no native
//            push SDK at all. Chrome receives a normal WEB push and
//            androidbrowserhelper's DelegationService renders it natively.
//            Android therefore depends entirely on the web path here plus
//            public/firebase-messaging-sw.js.
//   Browser- same web path as Android, shown in-page.
//
// Because Android relies on it, the web path must not be removed even if desktop
// browser push is considered unimportant.
//
// Native bridge contract (iOS wrapper, see Solidarity/WebView.swift):
//   PWA -> native   window.webkit.messageHandlers.<name>.postMessage(payload)
//     notificationToggle       { enabled: boolean, userId: string | null }
//     push-permission-request  ""   -> prompts, replies "granted" | "denied"
//     push-permission-state    ""   -> replies with the current status
//     push-token               ""   -> replies with the FCM registration token
//   native -> PWA   window CustomEvent of the same name, value in event.detail
const NATIVE_NOTIFICATION_HANDLER = "notificationToggle";
const NATIVE_NOTIFICATION_METHOD = "onNotificationToggle";
const NATIVE_PERMISSION_REQUEST_HANDLER = "push-permission-request";
const NATIVE_PERMISSION_STATE_HANDLER = "push-permission-state";
const NATIVE_TOKEN_HANDLER = "push-token";

// The user's explicit "off" choice. Turning notifications off leaves the OS
// permission granted, so permission alone cannot tell us how the toggle reads.
const OPT_OUT_KEY = "solidarity.push.optedOut";
// Last token we successfully registered, so a rotation is not re-sent forever.
const LAST_REGISTERED_KEY = "solidarity.push.lastRegisteredToken";
// Locale that token was last registered under. The backend moves the device
// between the -en and -ar broadcast topics on registration, so a language
// switch has to re-register even though the token itself has not changed.
const LAST_REGISTERED_LOCALE_KEY = "solidarity.push.lastRegisteredLocale";
// CPR the token was last registered under. A registration belongs to a user,
// not just a device: the backend keys UserDeviceTokens on (UserId, Token) and
// /send fans out to whatever tokens that user owns. So if the signed-in CPR
// changes, this device must re-register — otherwise it keeps receiving pushes
// addressed to whoever was signed in before.
const LAST_REGISTERED_CPR_KEY = "solidarity.push.lastRegisteredCpr";

const NATIVE_REPLY_TIMEOUT_MS = 5000;
const AUTO_PROMPT_DELAY_MS = 1200;

// Web Push (VAPID) public key — Firebase console -> Project settings ->
// Cloud Messaging -> Web configuration -> "Web Push certificates".
// This is a PUBLIC key and is safe to ship in the bundle. Without it getToken()
// throws and NO Android or browser notification can ever be delivered, so the
// value is checked loudly at startup rather than failing quietly.
const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY || "REPLACE_WITH_VAPID_PUBLIC_KEY";

const SERVICE_WORKER_URL = "/firebase-messaging-sw.js";

const hasVapidKey = () =>
  Boolean(VAPID_PUBLIC_KEY) &&
  VAPID_PUBLIC_KEY !== "REPLACE_WITH_VAPID_PUBLIC_KEY";

// iOS states under which the device actually receives push. `provisional` is
// included because iOS does deliver those — silently, straight to Notification
// Center — which is why it is NOT treated as "already answered" when prompting.
const GRANTED_STATES = ["authorized", "provisional", "ephemeral"];

const readStore = () => {
  try {
    return store.getState()?.login_register?.tokenInfo || null;
  } catch (e) {
    return null;
  }
};

const getLoggedInUserId = () => {
  let cpr = readStore()?.userName;
  if (!cpr) {
    // Same lazy-hydration caveat as getAuthToken.
    try {
      cpr = getLoggedInUserDataFromLocalStorage()?.userName;
    } catch (e) {
      cpr = null;
    }
  }
  return cpr ? String(cpr) : null;
};

// Whether a request made right now would carry a bearer token.
//
// Redux is the live source but it is hydrated from localStorage lazily (see
// useRefreshLogin), so on a fresh page load it can still be null while a
// perfectly good session exists. localStorage is what the axios interceptor
// actually authenticates from, so falling back to it keeps this gate in step
// with what the request will really do.
const getAuthToken = () => {
  const fromStore = readStore()?.access_token;
  if (fromStore) return fromStore;
  try {
    return getLoggedInUserDataFromLocalStorage()?.access_token || null;
  } catch (e) {
    return null;
  }
};

// True only inside the iOS wrapper, and only when it exposes the handlers this
// module needs. Browsers and older app builds fall through to the no-op path.
export const isNativeWrapper = () => {
  try {
    const handlers = window?.webkit?.messageHandlers;
    return Boolean(
      handlers &&
        handlers[NATIVE_NOTIFICATION_HANDLER] &&
        handlers[NATIVE_PERMISSION_REQUEST_HANDLER]
    );
  } catch (e) {
    return false;
  }
};

const postToNative = (handler, payload = "") => {
  try {
    const target = window?.webkit?.messageHandlers?.[handler];
    if (target && typeof target.postMessage === "function") {
      target.postMessage(payload);
      return true;
    }
  } catch (e) {
    // Not running inside the iOS wrapper — ignore.
  }
  return false;
};

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeFlag = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) {
    // Storage unavailable (private mode) — state just won't persist.
  }
};

const isOptedOut = () => readFlag(OPT_OUT_KEY) === "true";
const setOptedOut = (optedOut) =>
  writeFlag(OPT_OUT_KEY, optedOut ? "true" : null);

// Posts `handler` to the wrapper and resolves with the detail of the next
// matching CustomEvent. Resolves null if the wrapper is absent or silent, so
// callers never hang.
const askNative = (handler, eventName, payload = "") =>
  new Promise((resolve) => {
    let settled = false;
    let timer = null;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      try {
        window.removeEventListener(eventName, onReply);
      } catch (e) {
        // ignore
      }
      resolve(value);
    };

    const onReply = (event) => finish(event?.detail ?? null);

    try {
      window.addEventListener(eventName, onReply);
    } catch (e) {
      resolve(null);
      return;
    }

    timer = setTimeout(() => finish(null), NATIVE_REPLY_TIMEOUT_MS);
    if (!postToNative(handler, payload)) finish(null);
  });

export const getNativePermissionState = () =>
  askNative(NATIVE_PERMISSION_STATE_HANDLER, "push-permission-state");

export const requestNativePushPermission = () =>
  askNative(NATIVE_PERMISSION_REQUEST_HANDLER, "push-permission-request");

export const requestNativeToken = () =>
  askNative(NATIVE_TOKEN_HANDLER, "push-token");

export const notifyNativeNotificationToggle = (enabled, userId) => {
  const payload = { enabled: Boolean(enabled), userId: userId || null };

  postToNative(NATIVE_NOTIFICATION_HANDLER, payload);

  // Android WebView JavascriptInterface. Android bridges accept a single
  // string argument, so we pass serialized JSON.
  try {
    const androidBridge = window?.AndroidBridge || window?.Android;
    if (
      androidBridge &&
      typeof androidBridge[NATIVE_NOTIFICATION_METHOD] === "function"
    ) {
      androidBridge[NATIVE_NOTIFICATION_METHOD](JSON.stringify(payload));
    }
  } catch (e) {
    // Not running inside the Android wrapper — ignore.
  }
};

// --- Web push (Android TWA + browsers) --------------------------------------

// Web Push is only usable over HTTPS, outside a cross-origin frame, and in a
// browser that actually implements it. WKWebView reports none of these, so this
// is false inside the iOS wrapper — which is correct, iOS uses the native path.
export const isWebPushSupported = () => {
  if (typeof window === "undefined") return false;
  if (isNativeWrapper()) return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  try {
    if (window.self !== window.top) return false;
  } catch (e) {
    return false;
  }
  return true;
};

let webRegistrationPromise = null;

const registerServiceWorker = () => {
  if (!webRegistrationPromise) {
    webRegistrationPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .catch((e) => {
        // The usual cause is the SPA fallback serving index.html for this path,
        // which makes the browser reject it as a service worker.
        console.warn(
          `Failed to register ${SERVICE_WORKER_URL} — push will not work:`,
          e?.message || e
        );
        webRegistrationPromise = null;
        return null;
      });
  }
  return webRegistrationPromise;
};

// Returns the FCM registration token for this browser, or null.
const getWebToken = async () => {
  if (!isWebPushSupported()) return null;
  if (!hasVapidKey()) {
    console.warn(
      "REACT_APP_FIREBASE_VAPID_KEY is not set — web and Android push cannot work. " +
        "Firebase console -> Project settings -> Cloud Messaging -> Web Push certificates."
    );
    return null;
  }
  if (Notification.permission !== "granted") return null;

  try {
    if (!(await isSupported())) return null;
    const registration = await registerServiceWorker();
    if (!registration) return null;
    return await getToken(getMessaging(app), {
      vapidKey: VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (e) {
    console.warn("Failed to obtain an FCM web token:", e?.message || e);
    return null;
  }
};

// --- Token registration -----------------------------------------------------
let latestDeviceToken = null;

// Outcome of the last successful /register-device, kept for QA. Every device
// token gets exactly three subscriptions and the backend owns all of them: the
// base broadcast topic, one locale topic, and the customer's cpr-{CPR} topic.
// After a language switch this is what you assert on to confirm the device
// actually moved between the locale pair — the `subscribedTo*` flags are all
// false under DryRun (Development), which is expected there and a real problem
// anywhere else.
let lastRegistration = null;

export const getPushRegistrationInfo = () => lastRegistration;

// The API takes "ios" | "android" | "web". Android is a Trusted Web Activity
// receiving a web push through Chrome, not a native FCM client, so it registers
// as "web" — there is no "android" case to detect here.
const currentPlatform = () => (isNativeWrapper() ? "ios" : "web");

// The language the OS tray should render for this device. The backend buckets
// the user's tokens by this value and fires one multicast per bucket, so it is
// what decides whether a push arrives in English or Arabic.
const currentLocale = () => toApiLocale(getLanguageInLocalStorage());

// Sends the token to the backend so notifications can be addressed to this
// device. The user is taken from the JWT, so this is pointless while signed
// out — an anonymous call just 401s.
//
// `force` re-sends even when nothing appears to have changed, which is what we
// want after a login: the token is the same but the user it now belongs to is
// not, and only the backend can see that from the bearer token.
const registerToken = async (token, { force = false, locale: override } = {}) => {
  if (!token || token === "ERROR GET TOKEN") return false;
  if (isOptedOut()) return false;
  if (!getAuthToken()) return false;

  // An explicit locale lets the language switcher register the language it is
  // moving to without depending on having already written localStorage.
  const locale = override ? toApiLocale(override) : currentLocale();
  const cpr = getLoggedInUserId();

  // A registration is identified by all three: the token, the CPR it belongs
  // to, and the locale it should be delivered in. A language switch keeps the
  // token but has to move the device between the -en and -ar topics, and an
  // account switch keeps the token but must re-bind it to the new user.
  if (
    !force &&
    readFlag(LAST_REGISTERED_KEY) === token &&
    readFlag(LAST_REGISTERED_LOCALE_KEY) === locale &&
    readFlag(LAST_REGISTERED_CPR_KEY) === cpr
  ) {
    return true;
  }

  try {
    const result = await registerDevice({
      token,
      platform: currentPlatform(),
      locale,
    });

    // Cache what the server actually resolved, not what we asked for. It
    // normalizes the value (ar-BH -> ar) and that resolved locale is what
    // decides which topic the device sits on. Caching our own guess would let a
    // disagreement short-circuit every later registration, stranding the device
    // on the wrong locale topic.
    const resolvedLocale = result?.locale || locale;
    if (result?.locale && result.locale !== locale) {
      console.warn(
        `Push locale mismatch: requested "${locale}", backend resolved "${result.locale}". ` +
          "Broadcasts will arrive in the resolved language."
      );
    }

    lastRegistration = {
      token,
      cpr,
      requestedLocale: locale,
      locale: resolvedLocale,
      broadcastTopic: result?.broadcastTopic || null,
      localeTopic: result?.localeTopic || null,
      // Derived server-side from the JWT's user, not sent by us. Null when the
      // account has no CPR on file.
      cprTopic: result?.cprTopic || null,
      subscribedToBroadcastTopic: Boolean(result?.subscribedToBroadcastTopic),
      subscribedToLocaleTopic: Boolean(result?.subscribedToLocaleTopic),
      subscribedToCprTopic: Boolean(result?.subscribedToCprTopic),
    };

    writeFlag(LAST_REGISTERED_KEY, token);
    writeFlag(LAST_REGISTERED_LOCALE_KEY, resolvedLocale);
    writeFlag(LAST_REGISTERED_CPR_KEY, cpr);
    return true;
  } catch (e) {
    // Leave the flags as they are so the next launch retries.
    console.warn("Failed to register push token:", e?.message || e);
    return false;
  }
};

// Re-registers the current token under the app's current language, moving the
// device onto the matching broadcast topic. Called from the language switcher;
// without it broadcasts keep arriving in the previous language.
export const syncPushLocale = async (locale) => {
  const token = latestDeviceToken || readFlag(LAST_REGISTERED_KEY);
  if (!token) return false;
  return registerToken(token, { force: true, locale });
};

// Drops the mapping server-side. Safe to call when nothing is registered.
//
// Must run BEFORE the session is cleared: the endpoint is JWT-authenticated and
// the token to remove is read from localStorage, so a logout that wipes storage
// first leaves the device registered and still receiving pushes.
export const unregisterPushToken = async () => {
  // Both of these are read synchronously, before the caller gets a chance to
  // tear the session down — see the note on unregisterDevice's `authToken`.
  const token = latestDeviceToken || readFlag(LAST_REGISTERED_KEY);
  const authToken = getAuthToken();
  writeFlag(LAST_REGISTERED_KEY, null);
  writeFlag(LAST_REGISTERED_LOCALE_KEY, null);
  writeFlag(LAST_REGISTERED_CPR_KEY, null);
  latestDeviceToken = null;
  lastRegistration = null;
  if (!token) return;
  try {
    await unregisterDevice({ token, authToken });
  } catch (e) {
    console.warn("Failed to unregister push token:", e?.message || e);
  }
};

// --- Subscription state -----------------------------------------------------
// The settings screens read state synchronously, so the wrapper's answer is
// cached here and refreshed asynchronously, with listeners notified on change.
let pushEnabled = false;
const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((callback) => {
    try {
      callback(pushEnabled);
    } catch (e) {
      // ignore
    }
  });
};

const refreshEnabled = async () => {
  let next = false;

  if (isNativeWrapper()) {
    const state = await getNativePermissionState();
    next = GRANTED_STATES.includes(state) && !isOptedOut();
  } else if (isWebPushSupported()) {
    next = Notification.permission === "granted" && !isOptedOut();
  }

  if (next !== pushEnabled) {
    pushEnabled = next;
    notifyListeners();
  }
  return pushEnabled;
};

// True wherever push can actually be delivered: the iOS wrapper via the native
// bridge, and Chrome/Android via web push. The settings toggle renders disabled
// when this is false.
export const isPushSupported = () => {
  if (typeof window === "undefined") return false;
  return isNativeWrapper() || isWebPushSupported();
};

export const isPushEnabled = () => pushEnabled;

export const onPushStateChange = (callback) => {
  listeners.add(callback);
  try {
    callback(pushEnabled);
  } catch (e) {
    // ignore
  }
  refreshEnabled().catch(() => {});
  return () => {
    listeners.delete(callback);
  };
};

export const setPushEnabled = async (enabled) => {
  if (!isPushSupported()) return false;

  setOptedOut(!enabled);

  // Web path: Android's TWA and desktop browsers both land here.
  if (!isNativeWrapper()) {
    if (!enabled) {
      await unregisterPushToken();
      latestDeviceToken = null;
      return refreshEnabled();
    }
    // Browsers require this to be called from a user gesture, which the
    // settings toggle satisfies.
    if (Notification.permission !== "granted") {
      try {
        await Notification.requestPermission();
      } catch (e) {
        console.warn("Notification.requestPermission failed:", e?.message || e);
      }
    }
    const webToken = await getWebToken();
    if (webToken) {
      latestDeviceToken = webToken;
      await registerToken(webToken, { force: true });
    }
    return refreshEnabled();
  }

  if (enabled) {
    // Ask for the OS prompt when it has not been answered yet. The native
    // toggle handler requests permission too, but doing it here lets us wait
    // for the real outcome and report it back to the caller.
    const state = await getNativePermissionState();
    if (!GRANTED_STATES.includes(state)) {
      await requestNativePushPermission();
    }
    notifyNativeNotificationToggle(true, getLoggedInUserId());
    // The wrapper emits push-token once FCM has issued one; ask explicitly so
    // an already-registered device registers without waiting for a rotation.
    const token = await requestNativeToken();
    if (token) {
      latestDeviceToken = token;
      await registerToken(token, { force: true });
    }
  } else {
    await unregisterPushToken();
    // Tells the wrapper to delete the FCM token, which stops delivery.
    notifyNativeNotificationToggle(false, getLoggedInUserId());
    latestDeviceToken = null;
  }

  return refreshEnabled();
};

// --- Foreground messages ----------------------------------------------------
// A push that lands while the tab is focused is NOT shown by the browser and
// never reaches the service worker, so without this the inbox would look stale
// until the next reload.
//
// Both languages ride along in the FCM `data` block on every send precisely so
// the app can pick the one matching the current UI language, rather than the
// one the device happened to register under.
export const pickLocalizedPush = (data = {}, language) => {
  const arabic = toApiLocale(language) === "ar";
  return {
    title: (arabic ? data.title_ar : data.title_en) || data.title_en || "",
    message:
      (arabic ? data.message_ar : data.message_en) || data.message_en || "",
    subtitle:
      (arabic ? data.subtitle_ar : data.subtitle_en) || data.subtitle_en || "",
    launchUrl: data.launch_url || "",
  };
};

const subscribeToForegroundMessages = (onNotification) => {
  if (!isWebPushSupported()) return () => {};
  try {
    return onMessage(getMessaging(app), (payload) => {
      const localized = pickLocalizedPush(
        payload?.data || {},
        getLanguageInLocalStorage()
      );
      try {
        onNotification(localized, payload);
      } catch (e) {
        // A listener throwing must not break FCM's subscription.
      }
    });
  } catch (e) {
    console.warn("Failed to subscribe to foreground push:", e?.message || e);
    return () => {};
  }
};

// --- Launch prompt ----------------------------------------------------------
let autoPromptStarted = false;

// Asks for notification permission on app open, inside the native wrapper only.
//
// Browsers are excluded: Safari requires a user gesture for permission
// requests, so an automatic call there is dropped and burns the one chance.
//
// Only prompts when iOS can still show the dialog. `denied` is skipped (the
// prompt would not appear anyway), `provisional` IS prompted because those
// notifications arrive silently with no banner until it is upgraded, and a user
// who explicitly turned the toggle off is left alone.
// Web equivalent of the launch prompt, which is what gets Android users asked.
//
// Chrome — the browser behind the Android TWA — does NOT require a user gesture
// for Notification.requestPermission(), so this works on app open. Safari and
// Firefox do require one and will reject the call; there the settings toggle
// remains the way in, which is why the result is never assumed.
//
// Granting here also drives the Android 13+ side: Chrome asks the app for
// POST_NOTIFICATIONS via notification delegation, handled by
// NotificationPermissionRequestActivity in the TWA's manifest.
//
// Only asks when the browser has not already decided ("default"), and never
// after the user has explicitly turned notifications off.
const maybeAutoPromptForWebPush = async () => {
  if (!isWebPushSupported() || isOptedOut()) return;
  if (Notification.permission !== "default") {
    await refreshEnabled();
    return;
  }

  let result;
  try {
    result = await Notification.requestPermission();
  } catch (e) {
    // Safari/Firefox reject a request made without a user gesture.
    return;
  }

  if (result === "granted") {
    const token = await getWebToken();
    if (token) {
      latestDeviceToken = token;
      await registerToken(token, { force: true });
    }
  }
  await refreshEnabled();
};

const maybeAutoPromptForPush = async () => {
  if (autoPromptStarted || !isNativeWrapper()) return;
  autoPromptStarted = true;

  if (isOptedOut()) return;

  const state = await getNativePermissionState();
  // null means the wrapper never answered — do nothing this launch.
  if (state === null) return;

  if (state !== "notDetermined" && state !== "provisional") {
    await refreshEnabled();
    return;
  }

  const result = await requestNativePushPermission();
  if (result === "granted") {
    notifyNativeNotificationToggle(true, getLoggedInUserId());
  }
  await refreshEnabled();
};

export default function usePushNotifications() {
  // An expired session is a logout the user did not ask for, and it has to
  // release the device the same way: the FCM registration is bound to a CPR, so
  // leaving it in place means the next person to sign in on this device keeps
  // receiving the previous user's notifications.
  //
  // This fires before the session is cleared, so the outgoing JWT is still
  // readable — and unregisterDevice uses keepalive so the request survives the
  // redirect that follows.
  useEffect(() => onSessionExpired(() => {
    unregisterPushToken().catch(() => {});
  }), []);

  useEffect(() => {
    // Web path (Android TWA + browsers). Permission can only be requested from a
    // user gesture, so this does not prompt — it just picks up a device that has
    // already granted it and makes sure the backend has a current token, since
    // FCM web tokens rotate.
    if (!isNativeWrapper()) {
      if (!isWebPushSupported() || isOptedOut()) return undefined;

      let cancelled = false;
      refreshEnabled().catch(() => {});

      // Already granted: just make sure the backend has a current token, since
      // FCM web tokens rotate. Not yet decided: ask, after the same short delay
      // the native path uses so the app has painted first.
      const webTimer = setTimeout(() => {
        if (cancelled) return;
        if (Notification.permission === "default") {
          maybeAutoPromptForWebPush().catch(() => {});
          return;
        }
        getWebToken()
          .then((token) => {
            if (cancelled || !token) return;
            latestDeviceToken = token;
            return registerToken(token);
          })
          .catch(() => {});
      }, AUTO_PROMPT_DELAY_MS);

      // Registration is JWT-authenticated and the backend derives the user from
      // the bearer token, so a token obtained before login cannot be registered
      // and has to be re-sent once a session exists.
      let lastWebUserId = getLoggedInUserId();
      const unsubscribeWeb = store.subscribe(() => {
        const userId = getLoggedInUserId();
        if (userId === lastWebUserId) return;
        lastWebUserId = userId;
        if (!userId || isOptedOut()) return;
        const known = latestDeviceToken || readFlag(LAST_REGISTERED_KEY);
        // No token yet is normal here: the launch pass runs before login and
        // getWebToken() returns null until permission is granted. Ask again now
        // rather than leaving the device unregistered until the next reload.
        Promise.resolve(known || getWebToken())
          .then((token) => {
            if (cancelled || !token) return;
            latestDeviceToken = token;
            return registerToken(token, { force: true });
          })
          .catch(() => {});
      });

      // Refresh the inbox when a push lands while the tab is focused — the
      // browser shows nothing in that case and the service worker never runs.
      const unsubscribeForeground = subscribeToForegroundMessages(() => {
        if (!getAuthToken()) return;
        try {
          store.dispatch(fetchNotifications());
        } catch (e) {
          // Store not ready — the next inbox open picks it up anyway.
        }
      });

      return () => {
        cancelled = true;
        clearTimeout(webTimer);
        unsubscribeWeb();
        try {
          unsubscribeForeground();
        } catch (e) {
          // ignore
        }
      };
    }

    // The wrapper pushes the FCM token here whenever it is issued or rotates.
    const onToken = (event) => {
      const token = event?.detail;
      if (!token || token === "ERROR GET TOKEN") return;
      latestDeviceToken = token;
      registerToken(token).catch(() => {});
    };
    window.addEventListener("push-token", onToken);

    const timer = setTimeout(() => {
      maybeAutoPromptForPush().catch(() => {});
    }, AUTO_PROMPT_DELAY_MS);

    // FCM addresses tokens, not users, and the backend reads the owner off the
    // JWT — so on login the token has to be re-registered under the new session.
    let lastUserId = getLoggedInUserId();
    const unsubscribe = store.subscribe(() => {
      const userId = getLoggedInUserId();
      if (userId === lastUserId) return;
      lastUserId = userId;
      if (!userId || isOptedOut()) return;
      const token = latestDeviceToken || readFlag(LAST_REGISTERED_KEY);
      if (token) registerToken(token, { force: true }).catch(() => {});
    });

    return () => {
      window.removeEventListener("push-token", onToken);
      clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  return null;
}
